import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Assessment from "@/pages/Assessment";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { LegalLinks } from "@/components/LegalLayout";
import { toast } from "sonner";
import {
  Ship, Menu, X, ArrowRight, Anchor, Lightbulb, Layers, Check, Star,
  Image as ImageIcon, Video, Film, MessageSquare, Mail, Megaphone, ShieldCheck,
  CalendarClock, TrendingDown, Sparkles, PackagePlus, Clock, Factory, Phone, Send, CheckCircle2,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1552160757-52790c6f4faf?crop=entropy&cs=srgb&fm=jpg&q=85";
const BOAT = "https://customer-assets-rejwkqb3.emergentagent.net/job_marine-campaigns/artifacts/eu910mw5_IMG_0969.jpg";
const MARINA = "https://images.unsplash.com/photo-1547205725-2a24bf99d0b8?crop=entropy&cs=srgb&fm=jpg&q=85";

const NAV = [
  { label: "Home", to: "#top" },
  { label: "60-Second Assessment", to: "#assessment" },
  { label: "Features", to: "#features" },
  { label: "How It Works", to: "#how-it-works" },
  { label: "Pricing", to: "#pricing" },
  { label: "Schedule a Demo", to: "#demo" },
];

const scrollTo = (hash) => {
  const el = document.querySelector(hash);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

function PublicNav({ onLogin }) {
  const [open, setOpen] = useState(false);
  const jump = (hash) => { setOpen(false); scrollTo(hash); };
  return (
    <header className="sticky top-0 z-50 glass-navy border-b border-white/10" data-testid="public-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <button className="flex items-center gap-2.5" onClick={() => jump("#top")} data-testid="public-brand">
            <div className="w-9 h-9 rounded-lg gold-gradient flex items-center justify-center shadow-md">
              <Ship className="w-5 h-5 text-navy" />
            </div>
            <div className="leading-tight text-left">
              <div className="text-lg font-bold text-white tracking-tight">Marine Ad AI</div>
              <div className="text-[10px] uppercase tracking-widest text-gold/80 -mt-0.5">For Boat Dealers</div>
            </div>
          </button>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <button key={n.to} onClick={() => jump(n.to)} className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-300 hover:text-white hover:bg-white/10 transition-colors" data-testid={`public-nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}>
                {n.label}
              </button>
            ))}
            <Button onClick={onLogin} className="ml-2 gold-gradient text-navy font-bold gold-sheen" data-testid="public-dealer-login">
              Dealer Login
            </Button>
          </nav>

          <button className="lg:hidden text-white" onClick={() => setOpen((o) => !o)} data-testid="public-menu-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="lg:hidden px-4 pb-4 flex flex-col gap-1 border-t border-white/10 pt-3">
          {NAV.map((n) => (
            <button key={n.to} onClick={() => jump(n.to)} className="text-left px-3 py-2 rounded-lg text-sm font-semibold text-slate-200 hover:bg-white/10">
              {n.label}
            </button>
          ))}
          <Button onClick={onLogin} className="mt-2 gold-gradient text-navy font-bold" data-testid="public-dealer-login-mobile">Dealer Login</Button>
        </nav>
      )}
    </header>
  );
}

const SectionTitle = ({ eyebrow, children }) => (
  <div className="text-center max-w-3xl mx-auto mb-12">
    {eyebrow && <div className="text-xs font-bold uppercase tracking-widest text-gold mb-3">{eyebrow}</div>}
    <h2 className="text-3xl sm:text-4xl font-bold text-navy leading-tight">{children}</h2>
  </div>
);

const OppRow = ({ icon: Icon, title, reason, priority, tone }) => (
  <div className="flex items-start gap-3 bg-white rounded-xl border border-border p-4">
    <div className="w-9 h-9 rounded-lg bg-navy/5 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-navy" /></div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-navy text-sm truncate">{title}</span>
        <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${tone}`}>{priority}</span>
      </div>
      <p className="text-xs text-muted-foreground mt-0.5">{reason}</p>
    </div>
    <span className="text-[10px] font-bold uppercase tracking-wider text-gold bg-gold/10 px-2 py-1 rounded-full shrink-0 self-center hidden sm:block">Create Campaign</span>
  </div>
);

const DEMO_EMPTY = { name: "", dealership_name: "", email: "", phone: "", num_locations: "", inventory_size: "", dms_used: "", website: "", message: "" };

const DemoField = ({ label, k, type = "text", placeholder, required, testid, form, set }) => (
  <div className="space-y-1.5">
    <Label className="text-navy">{label}{required && <span className="text-gold"> *</span>}</Label>
    <Input type={type} value={form[k]} onChange={set(k)} placeholder={placeholder} data-testid={testid} className="bg-white" />
  </div>
);

function DemoForm({ assessmentId = "" }) {
  const [form, setForm] = useState(DEMO_EMPTY);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast.error("Please add your name and email");
    setSaving(true);
    try {
      await api.post("/demo-requests", { ...form, assessment_id: assessmentId || undefined });
      setDone(true);
      setForm(DEMO_EMPTY);
      toast.success("Demo request received. We will be in touch shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white rounded-2xl p-10 text-center" data-testid="demo-success">
        <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-7 h-7 text-emerald-600" />
        </div>
        <h3 className="text-2xl font-bold text-navy mb-2">Thanks, your demo request is in</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          Our team will reach out shortly to schedule a walkthrough using a real boat from your dealership.
        </p>
        <Button onClick={() => setDone(false)} variant="outline" className="mt-6" data-testid="demo-another">Submit another request</Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl p-6 sm:p-8 text-left" data-testid="demo-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <DemoField label="Name" k="name" required placeholder="Your full name" testid="demo-name" form={form} set={set} />
        <DemoField label="Dealership Name" k="dealership_name" placeholder="e.g. Apex Marine Group" testid="demo-dealership" form={form} set={set} />
        <DemoField label="Email" k="email" type="email" required placeholder="you@dealership.com" testid="demo-email" form={form} set={set} />
        <DemoField label="Phone" k="phone" type="tel" placeholder="(555) 555-0100" testid="demo-phone" form={form} set={set} />
        <DemoField label="Number of Locations" k="num_locations" type="number" placeholder="e.g. 2" testid="demo-locations" form={form} set={set} />
        <DemoField label="Approximate Active Inventory Size" k="inventory_size" type="number" placeholder="e.g. 40 active boats" testid="demo-inventory" form={form} set={set} />
        <DemoField label="DMS Used" k="dms_used" placeholder="e.g. DealerSpike, none" testid="demo-dms" form={form} set={set} />
        <DemoField label="Website" k="website" placeholder="https://yourdealership.com" testid="demo-website" form={form} set={set} />
      </div>
      <div className="space-y-1.5 mt-4">
        <Label className="text-navy">Message</Label>
        <Textarea value={form.message} onChange={set("message")} rows={3} placeholder="Tell us about a boat you would like to see marketed" data-testid="demo-message" className="bg-white" />
      </div>
      <Button type="submit" disabled={saving} className="mt-6 w-full sm:w-auto gold-gradient text-navy font-bold h-12 px-8 gold-sheen" data-testid="demo-submit">
        <Send className="w-4 h-4 mr-2" /> {saving ? "Sending..." : "Request My Demo"}
      </Button>
    </form>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const goLogin = () => navigate("/login");
  const [assessmentId, setAssessmentId] = useState("");

  return (
    <div id="top" className="min-h-screen bg-background" data-testid="landing-page">
      <PublicNav onLogin={goLogin} />

      {/* HERO */}
      <section className="relative overflow-hidden navy-gradient">
        <img src={HERO} alt="Boat moving across open water" className="absolute inset-0 w-full h-full object-cover opacity-25 animate-kenburns" />
        <div className="absolute inset-0 navy-gradient opacity-70" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-24 sm:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gold bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-6">
              <Anchor className="w-3.5 h-3.5" /> Built specifically for boat dealers
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] mb-6">
              Turn Your Inventory Into Marketing That Moves Boats
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-8 max-w-2xl">
              Marine Ad AI helps boat dealers identify what needs marketing, create complete campaigns from real inventory and real boat photography, and automate repetitive marketing tasks from one platform.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => scrollTo("#demo")} className="gold-gradient text-navy font-bold h-12 px-6 gold-sheen" data-testid="hero-schedule-demo">
                Schedule a Demo <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              <Button onClick={() => scrollTo("#how-it-works")} variant="outline" className="h-12 px-6 border-white/30 text-white bg-white/5 hover:bg-white/15 hover:text-white font-semibold" data-testid="hero-how-it-works">
                See How It Works
              </Button>
              <Button onClick={goLogin} variant="ghost" className="h-12 px-6 text-white hover:bg-white/10 hover:text-white font-semibold" data-testid="hero-dealer-login">
                Dealer Login
              </Button>
            </div>
          </div>
        </div>
        {/* animated ocean waves + value strip */}
        <div className="relative z-10 border-t border-white/10 bg-navy-surface/60">
          <div className="ocean-waves" style={{ bottom: "100%" }} aria-hidden="true" data-testid="hero-ocean-waves" />
          <div className="ocean-waves-2" style={{ bottom: "100%" }} aria-hidden="true" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 grid grid-cols-2 md:grid-cols-4 gap-y-3 gap-x-6 text-center">
            {["Built for Boat Dealers", "Real Boat Photography Only", "Inventory-Driven Marketing", "Marketing Automation"].map((v) => (
              <div key={v} className="text-xs sm:text-sm font-semibold text-slate-200 flex items-center justify-center gap-2">
                <Check className="w-4 h-4 text-gold shrink-0" /> {v}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 60-SECOND ASSESSMENT */}
      <Assessment
        onScheduleDemo={(id) => { setAssessmentId(id); scrollTo("#demo"); }}
        onExplore={() => scrollTo("#features")}
      />

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionTitle eyebrow="How It Works">From lot to launched marketing in three steps</SectionTitle>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { n: "01", icon: PackagePlus, title: "Bring In Your Inventory", body: "Add your boats manually today with full specs and real photography. Supported DMS integrations will eventually allow automatic inventory synchronization." },
              { n: "02", icon: Lightbulb, title: "Marine Ad AI Finds the Opportunities", body: "The platform helps identify new arrivals, aging inventory, price reductions, promotional pricing, boats not marketed recently, boat shows, dealer events, and manufacturer promotions." },
              { n: "03", icon: Layers, title: "Create, Approve, and Use the Campaign", body: "Marine Ad AI builds a complete marketing package from your inventory data and approved real boat photography, ready for you to review and use." },
            ].map((s) => (
              <div key={s.n} className="bg-white rounded-2xl border border-border p-7 card-hover">
                <div className="flex items-center justify-between mb-5">
                  <div className="w-11 h-11 rounded-xl gold-gradient flex items-center justify-center"><s.icon className="w-5 h-5 text-navy" /></div>
                  <span className="font-bold text-4xl text-navy/10">{s.n}</span>
                </div>
                <h3 className="text-xl font-bold text-navy mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARKETING OPPORTUNITIES */}
      <section className="py-20 sm:py-28 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-gold mb-3">Marketing Opportunities</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-navy leading-tight mb-5">
              Know What Needs Marketing Before It Gets Forgotten
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Marine Ad AI reviews your inventory and calendar and surfaces the boats and events that deserve attention right now, so nothing slips through the cracks.
            </p>
            <div className="inline-flex items-center gap-2 text-navy font-semibold bg-white border border-border rounded-full px-4 py-2">
              <Sparkles className="w-4 h-4 text-gold" /> Less remembering. Less repetitive work. More consistent marketing.
            </div>
          </div>

          {/* dashboard-style example */}
          <div className="rounded-2xl border border-border bg-white shadow-xl overflow-hidden" data-testid="opportunities-preview">
            <div className="navy-gradient p-6 text-white">
              <div className="text-sm text-slate-300">Good morning, Scott's Marine Group</div>
              <div className="text-2xl font-bold mt-1">4 Marketing Opportunities</div>
            </div>
            <div className="p-4 space-y-3">
              <OppRow icon={PackagePlus} title="2027 Blazer Bay Makaira 2800" reason="New arrival. No campaign has been created yet." priority="High" tone="bg-red-50 text-red-700" />
              <OppRow icon={CalendarClock} title="2024 Bay Boat 220" reason="96 days in inventory. Last campaign 31 days ago." priority="Medium" tone="bg-amber-50 text-amber-700" />
              <OppRow icon={TrendingDown} title="2025 Center Console 250" reason="Advertised price reduced. Promote the new price now." priority="High" tone="bg-red-50 text-red-700" />
              <OppRow icon={CalendarClock} title="Atlanta Boat Show" reason="21 days away. Begin pre-show awareness campaign." priority="Medium" tone="bg-amber-50 text-amber-700" />
            </div>
          </div>
        </div>
      </section>

      {/* MARKETING AUTOMATION */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionTitle eyebrow="Marketing Automation">Your Inventory Changes. Your Marketing Keeps Up.</SectionTitle>
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-center gap-3">
            {[
              { label: "Inventory Changes", soon: false },
              { label: "Marketing Opportunity Identified", soon: false },
              { label: "Campaign Prepared", soon: false },
              { label: "Dealer Reviews and Approves", soon: false },
              { label: "Campaign Published", soon: true },
            ].map((step, i, arr) => (
              <React.Fragment key={step.label}>
                <div className={`relative flex-1 rounded-2xl border p-5 text-center ${step.soon ? "border-dashed border-gold/60 bg-gold/5" : "border-border bg-white"}`}>
                  <div className="text-sm font-semibold text-navy">{step.label}</div>
                  {step.soon && <span className="mt-2 inline-block text-[10px] font-bold uppercase tracking-wider text-gold bg-gold/15 px-2 py-0.5 rounded-full">On the roadmap</span>}
                </div>
                {i < arr.length - 1 && <ArrowRight className="w-5 h-5 text-gold mx-auto rotate-90 lg:rotate-0 shrink-0" />}
              </React.Fragment>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-6 max-w-2xl mx-auto">
            Today, dealers review and approve every campaign before it is used. Automated publishing is part of the future roadmap and is not yet available.
          </p>
        </div>
      </section>

      {/* CAMPAIGN OUTPUT (features) */}
      <section id="features" className="py-20 sm:py-28 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionTitle eyebrow="Campaign Output">One Boat. A Complete Marketing Campaign.</SectionTitle>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="relative rounded-2xl overflow-hidden border border-border shadow-xl">
              <img src={BOAT} alt="Real dealer boat used for marketing" className="w-full h-full object-cover aspect-[4/3]" />
              <div className="absolute bottom-0 left-0 right-0 navy-gradient/0 bg-gradient-to-t from-navy/80 to-transparent p-5">
                <span className="text-[10px] font-bold uppercase tracking-widest text-gold">Real boat photography only</span>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { icon: ImageIcon, t: "Multiple static social creatives" },
                { icon: Film, t: "Story and Reel cover" },
                { icon: Video, t: "Vertical social video" },
                { icon: Megaphone, t: "Facebook and Instagram ad copy" },
                { icon: MessageSquare, t: "Organic social caption" },
                { icon: Mail, t: "Email marketing copy" },
                { icon: MessageSquare, t: "SMS marketing copy" },
                { icon: Layers, t: "Campaign Library and reuse" },
              ].map((f) => (
                <div key={f.t} className="flex items-center gap-3 bg-white rounded-xl border border-border p-4">
                  <div className="w-9 h-9 rounded-lg bg-navy/5 flex items-center justify-center shrink-0"><f.icon className="w-4 h-4 text-navy" /></div>
                  <span className="text-sm font-medium text-navy">{f.t}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Your boats stay your boats */}
          <div className="mt-12 rounded-2xl navy-gradient p-8 sm:p-10 text-center max-w-4xl mx-auto">
            <ShieldCheck className="w-9 h-9 text-gold mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-3">Your Boats Stay Your Boats</h3>
            <p className="text-slate-300 leading-relaxed max-w-2xl mx-auto">
              Marine Ad AI never generates fake boat imagery. Marketing creatives use real dealer-uploaded, manufacturer-supplied, or approved linked boat photography.
            </p>
          </div>
        </div>
      </section>

      {/* AVAILABLE NOW vs COMING SOON */}
      <section id="available" className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <SectionTitle eyebrow="What You Get">Available now, with more on the way</SectionTitle>
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border-2 border-border bg-white p-7" data-testid="available-now">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center"><Check className="w-4 h-4 text-emerald-600" /></div>
                <h3 className="text-xl font-bold text-navy">Available Now</h3>
              </div>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {["Manual inventory management", "AI-assisted campaign generation", "Multiple creative concepts", "Static social graphics", "Story and Reel creative", "Vertical social video", "Facebook and Instagram copy", "Organic social captions", "Email copy", "SMS copy", "Marketing Opportunities", "Marketing Calendar", "Campaign Library", "Downloadable marketing assets", "Dealer branding"].map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-navy/90"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /><span>{f}</span></li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border-2 border-dashed border-gold/50 bg-gold/5 p-7" data-testid="coming-soon">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-9 h-9 rounded-lg bg-gold/15 flex items-center justify-center"><Clock className="w-4 h-4 text-gold" /></div>
                <h3 className="text-xl font-bold text-navy">Coming Soon</h3>
              </div>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {["DMS inventory synchronization", "Direct Facebook and Instagram publishing", "Meta advertising integration", "Google advertising integration", "Automated campaign scheduling", "Expanded marketing automation", "Manufacturer-approved content integration", "Advanced campaign performance recommendations"].map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-navy/80"><Clock className="w-4 h-4 text-gold/70 mt-0.5 shrink-0" /><span>{f}</span></li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground mt-5">These features are planned and are not yet available.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-20 sm:py-28 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionTitle eyebrow="Pricing">Powerful Marine Marketing Without the Agency Overhead</SectionTitle>
          <div className="grid gap-6 lg:grid-cols-3 items-stretch">
            {/* DEALER */}
            <div className="relative rounded-2xl border-2 border-border bg-white p-7 flex flex-col" data-testid="landing-plan-dealer">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Dealer</div>
              <div className="mt-1 flex items-baseline gap-1"><span className="text-4xl font-bold text-navy">$299</span><span className="text-sm text-muted-foreground">/month</span></div>
              <p className="text-sm text-navy/70 mt-2 font-medium">Best for dealerships with up to 25 active boats.</p>
              <ul className="space-y-2.5 flex-1 mt-5">
                {["Core campaign generation", "Multiple creative concepts", "Static social graphics", "Story and Reel creative", "Vertical social video", "Facebook and Instagram copy", "Organic social captions", "Email marketing copy", "SMS marketing copy", "Marketing Opportunities", "Marketing Calendar", "Campaign Library", "Manual inventory management", "Dealer branding", "Downloadable creatives"].map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-navy/90"><Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /><span>{f}</span></li>
                ))}
              </ul>
              <Button onClick={() => scrollTo("#demo")} className="mt-6 w-full bg-navy hover:bg-navy-light text-white h-11 font-bold" data-testid="landing-choose-dealer">Schedule a Demo</Button>
            </div>

            {/* DEALER PRO */}
            <div className="relative rounded-2xl border-2 border-gold bg-white p-7 flex flex-col ring-2 ring-gold/20 shadow-lg shadow-gold/10" data-testid="landing-plan-dealer-pro">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 gold-gradient text-navy text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow"><Star className="w-3 h-3" /> Most Popular</span>
              <div className="text-xs uppercase tracking-widest text-gold font-semibold">Dealer Pro</div>
              <div className="mt-1 flex items-baseline gap-1"><span className="text-4xl font-bold text-navy">$399</span><span className="text-sm text-muted-foreground">/month</span></div>
              <p className="text-sm text-navy/70 mt-2 font-medium">Best for dealerships with 26 to 75 active boats.</p>
              <div className="text-xs font-semibold uppercase tracking-widest text-navy mt-5 mb-2">Everything in Dealer, plus</div>
              <ul className="space-y-2.5 flex-1">
                {["DMS inventory synchronization", "Expanded team access", "Higher campaign and video usage", "Advanced Marketing Opportunities", "Future social publishing", "Future Meta advertising integration", "Future Google advertising integration", "Expanded automation"].map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-navy/90"><Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /><span>{f}</span><span className="text-[10px] font-semibold text-muted-foreground bg-secondary px-1.5 py-0.5 rounded ml-auto shrink-0 self-center">Soon</span></li>
                ))}
              </ul>
              <Button onClick={() => scrollTo("#demo")} className="mt-6 w-full gold-gradient text-navy h-11 font-bold gold-sheen" data-testid="landing-choose-dealer-pro">Schedule a Demo</Button>
            </div>

            {/* DEALER ENTERPRISE */}
            <div className="relative rounded-2xl border-2 border-navy/20 bg-navy p-7 flex flex-col text-white" data-testid="landing-plan-dealer-enterprise">
              <div className="text-xs uppercase tracking-widest text-gold font-semibold">Dealer Enterprise</div>
              <div className="mt-1 flex items-baseline gap-1"><span className="text-4xl font-bold text-white">Custom</span><span className="text-sm text-slate-300">pricing</span></div>
              <p className="text-sm text-slate-300 mt-2 font-medium">Best for high-volume dealers, dealer groups, multiple locations, or 76+ active boats.</p>
              <ul className="space-y-2.5 flex-1 mt-5">
                {["Large inventory support", "Multiple locations", "Expanded user access", "Centralized dealership branding", "Higher campaign usage", "Higher video and rendering usage", "Advanced Marketing Opportunities", "Future DMS synchronization", "Future social publishing", "Future advertising integrations", "Priority support", "Consolidated reporting"].map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-slate-200"><Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /><span>{f}</span></li>
                ))}
              </ul>
              <a href="mailto:sales@marineadai.com?subject=Marine%20Ad%20AI%20Dealer%20Enterprise%20Inquiry" className="mt-6" data-testid="landing-contact-sales-enterprise">
                <Button className="w-full gold-gradient text-navy h-11 font-bold gold-sheen">Contact Sales</Button>
              </a>
            </div>
          </div>
          <p className="text-xs text-muted-foreground text-center mt-6">
            Plan tiers are based on active inventory count, not total historical inventory records. Early-access pricing and features may change before commercial launch. Advertising spend, if applicable in the future, will be separate from the monthly platform subscription.
          </p>
        </div>
      </section>

      {/* MANUFACTURER / OEM */}
      <section id="manufacturers" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-gold mb-3">Manufacturer / OEM</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-navy leading-tight mb-5">Built for Manufacturers Too</h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Give your dealer network access to brand-approved marketing content while maintaining control of product imagery, specifications, messaging, and brand standards.
            </p>
            <div className="text-xs font-semibold uppercase tracking-widest text-navy mb-3">Future capabilities</div>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
              {["Approved brand assets", "Approved product photography", "Model specifications", "Approved marketing claims", "Dealer campaign distribution", "Localized dealer campaigns", "Manufacturer-supported promotions", "Dealer network reporting"].map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-navy/80"><Clock className="w-4 h-4 text-gold/70 mt-0.5 shrink-0" /><span>{f}</span></li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border-2 border-navy/20 bg-navy p-8 text-white text-center" data-testid="landing-plan-manufacturer">
            <div className="w-12 h-12 rounded-xl gold-gradient flex items-center justify-center mx-auto mb-5"><Factory className="w-6 h-6 text-navy" /></div>
            <div className="text-xs uppercase tracking-widest text-gold font-semibold">Manufacturer / OEM</div>
            <div className="mt-2 text-4xl font-bold text-white">Custom Pricing</div>
            <p className="text-sm text-slate-300 mt-3 mb-7 max-w-xs mx-auto">
              Tailored programs for manufacturers supporting a network of dealers.
            </p>
            <a href="mailto:sales@marineadai.com?subject=Marine%20Ad%20AI%20Manufacturer%20OEM%20Inquiry" data-testid="landing-contact-sales-manufacturer">
              <Button className="w-full gold-gradient text-navy h-12 font-bold gold-sheen"><Phone className="w-4 h-4 mr-1.5" /> Contact Sales</Button>
            </a>
          </div>
        </div>
      </section>

      {/* DEMO SECTION */}
      <section id="demo" className="relative overflow-hidden navy-gradient py-20 sm:py-28">
        <img src={MARINA} alt="Boats at a marina" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 navy-gradient opacity-70" />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight mb-4">See What Marine Ad AI Can Do With One of Your Boats</h2>
            <p className="text-slate-300 leading-relaxed max-w-2xl mx-auto">
              Bring us one real boat from your dealership and we will show you how Marine Ad AI can turn it into a complete marketing campaign.
            </p>
          </div>
          <DemoForm assessmentId={assessmentId} />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 sm:py-28 bg-secondary/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-navy leading-tight mb-4">Spend Less Time Building Marketing. Spend More Time Selling Boats.</h2>
          <p className="text-muted-foreground leading-relaxed mb-8">
            Marine Ad AI helps boat dealers turn inventory into consistent, professional marketing with less repetitive work.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button onClick={() => scrollTo("#demo")} className="gold-gradient text-navy font-bold h-12 px-7 gold-sheen" data-testid="final-schedule-demo">
              Schedule a Demo <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <Button onClick={goLogin} variant="outline" className="h-12 px-7 border-navy/20 text-navy hover:bg-navy hover:text-white font-semibold" data-testid="final-dealer-login">
              Dealer Login
            </Button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-navy-surface text-slate-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg gold-gradient flex items-center justify-center"><Ship className="w-4 h-4 text-navy" /></div>
            <span className="text-white font-bold">Marine Ad AI</span>
          </div>
          <div className="text-sm">Inventory-driven marketing and automation, built for boat dealers.</div>
          <div className="flex items-center gap-4">
            <LegalLinks testPrefix="landing-footer" />
            <button onClick={goLogin} className="text-gold font-semibold text-sm hover:underline" data-testid="footer-dealer-login">Dealer Login</button>
          </div>
        </div>
        <div className="border-t border-white/10 text-center text-xs py-4 px-4" data-testid="landing-footer-company">Marine Ad AI is a software platform of RecJet Technologies.</div>
      </footer>
    </div>
  );
}
